import { authedFetch } from '@proj-airi/stage-ui/libs/auth-fetch'
import { SERVER_URL } from '@proj-airi/stage-ui/libs/server'
import { useAuthStore } from '@proj-airi/stage-ui/stores/auth'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  createHoloRuntimeLeaseClient,
  HoloRuntimeLeaseError,
} from './holo-runtime-lease-client.mjs'
import { getOrCreateHoloClientRuntimeId } from './holo-runtime-domain.mjs'

export type HoloRuntimePhase
  = | 'idle'
    | 'preparing'
    | 'passive'
    | 'activating'
    | 'active'
    | 'blocked'
    | 'error'

export interface HoloRuntimeLease {
  runtime_lease_id: string
  user_id: string
  character_id: string
  client_runtime_id: string
  generation: number
  opened_at: number
  renewed_at: number
  expires_at: number
}

function errorCode(error: unknown) {
  if (error instanceof HoloRuntimeLeaseError)
    return error.code || `http_${error.status ?? 'unknown'}`
  return error instanceof Error ? error.message : String(error)
}

export const useHoloRuntimeAuthorityStore = defineStore('holo-runtime-authority', () => {
  const auth = useAuthStore()
  const clientRuntimeId = ref(getOrCreateHoloClientRuntimeId())
  const phase = ref<HoloRuntimePhase>('idle')
  const characterId = ref<string | null>(null)
  const runtimeLease = ref<HoloRuntimeLease | null>(null)
  const lastError = ref<string | null>(null)

  // Authentication/Character intent fences requests from a previous account or role.
  let requestEpoch = 0
  function isCurrent(ticket: number, id: string, owner: string) {
    return ticket === requestEpoch
      && characterId.value === id
      && auth.userId === owner
      && auth.isAuthenticated
  }

  const client = createHoloRuntimeLeaseClient({
    baseUrl: SERVER_URL,
    fetchImpl: authedFetch,
  })

  const isActive = computed(() => {
    return phase.value === 'active'
      && auth.isAuthenticated
      && runtimeLease.value?.user_id === auth.userId
      && runtimeLease.value?.client_runtime_id === clientRuntimeId.value
      && runtimeLease.value?.character_id === characterId.value
  })

  const heldByAnotherRuntime = computed(() => {
    return !!runtimeLease.value
      && runtimeLease.value.client_runtime_id !== clientRuntimeId.value
  })

  const isRunnable = computed(() => {
    return phase.value !== 'blocked'
      && phase.value !== 'error'
      && !!characterId.value
  })

  function applyCurrent(activation: { character_id: string, runtime_lease: HoloRuntimeLease | null }) {
    characterId.value = activation.character_id
    runtimeLease.value = activation.runtime_lease
    phase.value = activation.runtime_lease?.client_runtime_id === clientRuntimeId.value
      && activation.runtime_lease?.character_id === activation.character_id
      ? 'active'
      : 'passive'
    lastError.value = null
  }

  function failClosed(error: unknown) {
    const code = errorCode(error)
    runtimeLease.value = null
    lastError.value = code
    phase.value = error instanceof HoloRuntimeLeaseError && error.code === 'character_not_found'
      ? 'blocked'
      : 'passive'
    return code
  }

  async function prepare(nextCharacterId: string) {
    const id = nextCharacterId?.trim()
    if (!id)
      throw new TypeError('characterId must be a non-empty string')

    const ticket = ++requestEpoch
    const owner = auth.userId
    phase.value = 'preparing'
    characterId.value = id
    // Keep the previous same-account lease until successful Character switch
    // or logout can revoke it. The preparing phase closes local execution.
    lastError.value = null

    try {
      const current = await client.current(id)
      if (!isCurrent(ticket, id, owner))
        return phase.value
      const currentLease = current.runtime_lease
      if (currentLease
        && currentLease.client_runtime_id === clientRuntimeId.value
        && currentLease.character_id === id) {
        try {
          const renewed = await client.renew({
            runtimeLeaseId: currentLease.runtime_lease_id,
            clientRuntimeId: clientRuntimeId.value,
            generation: currentLease.generation,
          })
          if (isCurrent(ticket, id, owner))
            applyCurrent(renewed)
          return phase.value
        }
        catch (error) {
          if (isCurrent(ticket, id, owner))
            failClosed(error)
          return phase.value
        }
      }

      if (isCurrent(ticket, id, owner))
        applyCurrent(current)
      return phase.value
    }
    catch (error) {
      if (!isCurrent(ticket, id, owner))
        return phase.value
      lastError.value = errorCode(error)
      runtimeLease.value = null
      phase.value = error instanceof HoloRuntimeLeaseError && error.code === 'character_not_found'
        ? 'blocked'
        : 'error'
      return phase.value
    }
  }

  async function activate() {
    const id = characterId.value
    if (!id)
      throw new Error('Stage Character is not prepared')
    if (isActive.value)
      return true
    if (heldByAnotherRuntime.value)
      return false

    const ticket = ++requestEpoch
    const owner = auth.userId
    phase.value = 'activating'
    lastError.value = null

    try {
      // A same-holder ACQUIRE atomically switches the account's Character and
      // rotates generation; it does not open an independent Character lease.
      const activation = await client.acquire({
        characterId: id,
        clientRuntimeId: clientRuntimeId.value,
      })
      if (!isCurrent(ticket, id, owner))
        return false
      applyCurrent(activation)
      return isActive.value
    }
    catch (error) {
      if (!isCurrent(ticket, id, owner))
        return false
      if (error instanceof HoloRuntimeLeaseError && error.code === 'runtime_lease_conflict') {
        await prepare(id)
        return false
      }
      failClosed(error)
      return false
    }
  }

  async function openCharacter(nextCharacterId: string) {
    const id = nextCharacterId?.trim()
    if (!id)
      throw new TypeError('characterId must be a non-empty string')

    // Preparing synchronously closes the previous Character gate; the server
    // retains account ownership while this holder switches Characters.
    await prepare(id)
    if (characterId.value !== id)
      return false
    if (isActive.value)
      return true
    if (phase.value !== 'passive' || heldByAnotherRuntime.value)
      return false

    return activate()
  }

  async function takeover() {
    const id = characterId.value
    if (!id)
      throw new Error('Stage Character is not prepared')
    if (isActive.value)
      return true
    const held = runtimeLease.value
    if (!held)
      return activate()

    const ticket = ++requestEpoch
    const owner = auth.userId
    phase.value = 'activating'
    lastError.value = null
    try {
      const activation = await client.takeover({
        expectedRuntimeLeaseId: held.runtime_lease_id,
        characterId: id,
        clientRuntimeId: clientRuntimeId.value,
      })
      if (!isCurrent(ticket, id, owner))
        return false
      applyCurrent(activation)
      return isActive.value
    }
    catch (error) {
      if (!isCurrent(ticket, id, owner))
        return false
      failClosed(error)
      await prepare(id).catch(() => {})
      return false
    }
  }

  async function renew() {
    const held = runtimeLease.value
    if (!held || held.client_runtime_id !== clientRuntimeId.value
      || held.character_id !== characterId.value || phase.value !== 'active')
      return false

    const ticket = requestEpoch
    const owner = auth.userId
    const id = held.character_id
    try {
      const activation = await client.renew({
        runtimeLeaseId: held.runtime_lease_id,
        clientRuntimeId: clientRuntimeId.value,
        generation: held.generation,
      })
      if (!isCurrent(ticket, id, owner) || runtimeLease.value?.runtime_lease_id !== held.runtime_lease_id)
        return false
      applyCurrent(activation)
      return true
    }
    catch (error) {
      if (isCurrent(ticket, id, owner) && runtimeLease.value?.runtime_lease_id === held.runtime_lease_id)
        failClosed(error)
      return false
    }
  }

  async function verifyCurrent() {
    // A takeover on another terminal is invisible to this browser until its
    // next renewal. Recheck trusted account authority before every new Chat
    // turn so the former holder cannot initiate another LLM operation.
    const held = runtimeLease.value
    if (!held || !isActive.value)
      return false
    const ticket = requestEpoch
    const owner = auth.userId
    const id = held.character_id
    try {
      const observed = await client.current(id)
      if (!isCurrent(ticket, id, owner) || runtimeLease.value?.runtime_lease_id !== held.runtime_lease_id)
        return false
      const remote = observed.runtime_lease
      if (!remote
        || remote.runtime_lease_id !== held.runtime_lease_id
        || remote.generation !== held.generation
        || remote.user_id !== owner
        || remote.character_id !== id
        || remote.client_runtime_id !== clientRuntimeId.value) {
        applyCurrent(observed)
        return false
      }
      return true
    }
    catch (error) {
      if (isCurrent(ticket, id, owner) && runtimeLease.value?.runtime_lease_id === held.runtime_lease_id)
        failClosed(error)
      return false
    }
  }

  async function release(reason = 'released') {
    const ticket = ++requestEpoch
    const held = runtimeLease.value
    phase.value = characterId.value ? 'passive' : 'idle'
    runtimeLease.value = null

    if (!held || held.client_runtime_id !== clientRuntimeId.value)
      return true

    try {
      await client.release({
        runtimeLeaseId: held.runtime_lease_id,
        clientRuntimeId: clientRuntimeId.value,
        generation: held.generation,
        reason,
      })
      if (ticket === requestEpoch)
        lastError.value = null
      return true
    }
    catch (error) {
      if (ticket === requestEpoch)
        lastError.value = errorCode(error)
      return false
    }
  }

  async function clear(reason = 'runtime-reset') {
    // Clear is synchronous up to its first await. A pending prior CURRENT or
    // ACQUIRE cannot re-admit the old account/Character after logout.
    ++requestEpoch
    const held = runtimeLease.value
    characterId.value = null
    runtimeLease.value = null
    lastError.value = null
    phase.value = 'idle'
    if (held && held.client_runtime_id === clientRuntimeId.value) {
      try {
        await client.release({
          runtimeLeaseId: held.runtime_lease_id,
          clientRuntimeId: clientRuntimeId.value,
          generation: held.generation,
          reason,
        })
      }
      catch {
        // The trusted 30s TTL bounds late/stale cleanup.
      }
    }
  }

  return {
    clientRuntimeId,
    phase,
    characterId,
    runtimeLease,
    lastError,
    isActive,
    heldByAnotherRuntime,
    isRunnable,
    prepare,
    activate,
    openCharacter,
    takeover,
    renew,
    verifyCurrent,
    release,
    clear,
  }
}, {
  synced: {
    state: true,
    actions: [
      'prepare',
      'activate',
      'openCharacter',
      'takeover',
      'renew',
      'release',
      'clear',
    ],
  },
})
